import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CreateSnapshotInputSchema } from '@/api/main/types.gen'

interface Props {
  opened: boolean
  onClose: () => void
  onSubmit: (values: CreateSnapshotInputSchema) => void
  snapshots: string[]
  isSubmitting: boolean
}

const SNAPSHOT_NAME_PATTERN = /^[a-z0-9_]+$/

type Step = 'form' | 'confirm'

export function CreateSnapshotModal({ opened, onClose, onSubmit, snapshots, isSubmitting }: Props) {
  const { t } = useTranslation('mapSnapshots')
  const [step, setStep] = useState<Step>('form')

  const defaultName = useMemo(() => format(new Date(), 'yyyy_MM_dd'), [])

  const form = useForm({
    initialValues: {
      snapshot_name: defaultName,
    },
    validate: {
      snapshot_name: value => {
        if (!value) return t('errNameRequired')
        if (!SNAPSHOT_NAME_PATTERN.test(value)) return t('errNamePattern')
        if (snapshots.includes(value)) return t('errNameTaken')
        return null
      },
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues({ snapshot_name: defaultName })
      form.resetDirty()
      setStep('form')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const handleClose = () => {
    setStep('form')
    onClose()
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t('createSnapshot')} padding="lg" size="lg" yOffset="5vh">
      <Stack style={{ minHeight: 280 }}>
        {step === 'form' ? (
          <form onSubmit={form.onSubmit(() => setStep('confirm'))}>
            <Stack>
              <TextInput
                label={t('snapshotName')}
                description={t('snapshotNameHelp')}
                {...form.getInputProps('snapshot_name')}
                data-autofocus
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="default" onClick={handleClose}>
                  {t('cancel')}
                </Button>
                <Button type="submit">{t('createSnapshot')}</Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack>
            <Text fw={600}>{t('confirmCreateTitle')}</Text>
            <Text size="sm">{t('confirmCreateBody', { name: form.values.snapshot_name })}</Text>
            <Group justify="flex-end" gap="xs" mt="md">
              <Button variant="default" onClick={() => setStep('form')}>
                {t('back')}
              </Button>
              <Button loading={isSubmitting} onClick={() => onSubmit(form.values)}>
                {t('confirm')}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
